import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Activity,
  AlertCircle,
  ArrowLeft,
  ArrowLeftRight,
  CheckCircle2,
  Clock,
  Gauge,
  Loader2,
  MessageCircle,
  Phone,
  Play,
  Send,
  Square,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useCall,
  useCallLatency,
  useCallTranscript,
  useSendCallFollowup,
} from "@/features/calls/hooks";
import type { Call, CallLatency } from "@/features/calls/api";
import CallSummaryCard from "@/features/calls/components/CallSummaryCard";
import LeadScoreCard from "@/features/calls/components/LeadScoreCard";
import ToolCallBlock from "@/features/agents/components/ToolCallBlock";
import { cn } from "@/lib/utils";

const STATUS_STYLES: Record<
  string,
  { label: string; cls: string; Icon: typeof CheckCircle2 }
> = {
  completed: {
    label: "Completed",
    cls: "text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-400",
    Icon: CheckCircle2,
  },
  failed: {
    label: "Failed",
    cls: "text-rose-600 bg-rose-50 dark:bg-rose-950/40 dark:text-rose-400",
    Icon: XCircle,
  },
  no_answer: {
    label: "No answer",
    cls: "text-amber-600 bg-amber-50 dark:bg-amber-950/40 dark:text-amber-400",
    Icon: AlertCircle,
  },
  in_progress: {
    label: "In progress",
    cls: "text-blue-600 bg-blue-50 dark:bg-blue-950/40 dark:text-blue-400",
    Icon: Activity,
  },
  pending: {
    label: "Pending",
    cls: "text-slate-600 bg-slate-100 dark:bg-slate-800 dark:text-slate-300",
    Icon: Clock,
  },
};

function StatusBadge({ status }: { status: string }) {
  const entry =
    STATUS_STYLES[status] ?? {
      label: status,
      cls: "text-muted-foreground bg-muted",
      Icon: Activity,
    };
  const Icon = entry.Icon;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
        entry.cls,
      )}
    >
      <Icon className="size-3" />
      {entry.label}
    </span>
  );
}

function fmtDate(iso?: string | null) {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (!Number.isFinite(t) || t === 0) return "—";
  return new Date(t).toLocaleString();
}

function fmtDuration(seconds: number | null | undefined) {
  const val = seconds ?? 0;
  const m = Math.floor(val / 60);
  const s = val % 60;
  return `${m}m ${s}s`;
}

export default function CallDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/calls")}
          aria-label="Back"
        >
          <ArrowLeft className="size-5" />
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">Call Details</h1>
      </div>
      <CallDetailContent id={id!} />
    </div>
  );
}

/** Everything below the page header — also embedded in the Assistant Logs
 * slide-over, so it must not assume it lives on the /calls/{id} route. */
export function CallDetailContent({ id }: { id: string }) {
  const callQ = useCall(id);
  const txQ = useCallTranscript(id);
  const latencyQ = useCallLatency(id);

  if (callQ.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (callQ.isError || !callQ.data) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-destructive">
          Call not found.
        </CardContent>
      </Card>
    );
  }

  const call = callQ.data;
  const tx = txQ.data;

  return (
    <div className="space-y-6">
      <CallInfoCard call={call} />

      <div className="grid gap-6 lg:grid-cols-[1fr_minmax(0,360px)]">
        <CallSummaryCard
          callId={call.id}
          hasTranscript={!!(call.transcript && call.transcript.trim())}
        />
        <LeadScoreCard
          call={call}
          hasTranscript={!!(call.transcript && call.transcript.trim())}
        />
      </div>

      <StructuredOutputsCard call={call} />
      <ApiRequestCard call={call} />

      <FollowupStatusCard call={call} />

      <LatencyCard data={latencyQ.data} loading={latencyQ.isLoading} />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Transcript</CardTitle>
        </CardHeader>
        <CardContent>
          {txQ.isLoading && <Skeleton className="h-32 w-full" />}
          {!txQ.isLoading && tx?.turns && tx.turns.length > 0 ? (
            <div className="space-y-2">
              {tx.turns.map((turn, i) => {
                const isAgent =
                  turn.role === "assistant" || turn.speaker === "agent";
                const text = turn.text || turn.content || "";
                const tools = turn.tool_calls ?? [];
                return (
                  <div
                    key={i}
                    className={cn(
                      "rounded-md p-3",
                      isAgent && "bg-muted/50",
                    )}
                  >
                    <p
                      className={cn(
                        "mb-1 text-[10px] font-semibold uppercase tracking-wide",
                        isAgent ? "text-primary" : "text-muted-foreground",
                      )}
                    >
                      {isAgent ? "Agent" : "User"}
                    </p>
                    {text && <p className="text-sm">{text}</p>}
                    {tools.length > 0 && (
                      <div className={cn("space-y-1.5", text && "mt-2")}>
                        {tools.map((c, j) => (
                          <ToolCallBlock key={j} call={c} />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : !txQ.isLoading && call.transcript ? (
            <pre className="whitespace-pre-wrap rounded-md bg-muted/50 p-3 text-sm leading-relaxed">
              {call.transcript}
            </pre>
          ) : (
            !txQ.isLoading && (
              <p className="text-sm text-muted-foreground">
                No transcript available.
              </p>
            )
          )}
        </CardContent>
      </Card>

    </div>
  );
}

function LatencyCard({
  data,
  loading,
}: {
  data: CallLatency | undefined;
  loading: boolean;
}) {
  // Don't render the card at all for calls with no measured latency (old calls,
  // failed/zero-turn calls). The data only exists for v3 calls after this shipped.
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Gauge className="size-4" /> Latency
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }
  if (!data || data.summary.turn_count === 0) return null;

  const s = data.summary;
  const fmt = (ms: number) =>
    ms >= 1000 ? `${(ms / 1000).toFixed(2)}s` : `${ms}ms`;
  const fmtOpt = (ms: number) => (ms > 0 ? fmt(ms) : "—");
  // v4 turns carry the full per-stage waterfall; older v3 rows only have the
  // LLM/TTS split. Prefer the richer breakdown when it's present.
  const hasStages = data.turns.some(
    (t) =>
      t.endpointing_ms > 0 ||
      t.stt_ms > 0 ||
      t.llm_ttft_ms > 0 ||
      t.tts_ttfb_ms > 0,
  );
  const hasSplit = !hasStages && (s.avg_llm_ms > 0 || s.avg_tts_ms > 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Gauge className="size-4" /> Latency
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          End-to-end response time — from the end of the caller's speech to the
          agent's first audible word, per turn.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <LatencyStat label="Avg E2E" value={fmt(s.avg_e2e_ms)} />
          <LatencyStat label="P50" value={fmt(s.p50_e2e_ms)} />
          <LatencyStat label="P95" value={fmt(s.p95_e2e_ms)} />
          <LatencyStat label="Slowest" value={fmt(s.max_e2e_ms)} />
          {hasSplit && (
            <LatencyStat label="Avg LLM" value={fmtOpt(s.avg_llm_ms)} />
          )}
          {hasSplit && (
            <LatencyStat label="Avg TTS" value={fmtOpt(s.avg_tts_ms)} />
          )}
        </div>

        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-xs">
            <thead className="border-b text-left text-[10px] uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">Turn</th>
                <th className="px-3 py-2 font-medium">Caller said</th>
                {hasStages && (
                  <th className="px-3 py-2 text-right font-medium">Endpoint</th>
                )}
                {hasStages && (
                  <th className="px-3 py-2 text-right font-medium">STT</th>
                )}
                {(hasStages || hasSplit) && (
                  <th className="px-3 py-2 text-right font-medium">LLM</th>
                )}
                {(hasStages || hasSplit) && (
                  <th className="px-3 py-2 text-right font-medium">TTS</th>
                )}
                <th className="px-3 py-2 text-right font-medium">E2E</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.turns.map((t) => (
                <tr key={t.turn_index}>
                  <td className="px-3 py-1.5 tabular-nums text-muted-foreground">
                    {t.turn_index}
                  </td>
                  <td className="max-w-[280px] truncate px-3 py-1.5">
                    {t.user_message || "—"}
                  </td>
                  {hasStages && (
                    <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">
                      {fmtOpt(t.endpointing_ms)}
                    </td>
                  )}
                  {hasStages && (
                    <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">
                      {fmtOpt(t.stt_ms)}
                    </td>
                  )}
                  {(hasStages || hasSplit) && (
                    <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">
                      {fmtOpt(hasStages ? t.llm_ttft_ms : t.llm_ms)}
                    </td>
                  )}
                  {(hasStages || hasSplit) && (
                    <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">
                      {fmtOpt(hasStages ? t.tts_ttfb_ms : t.tts_ms)}
                    </td>
                  )}
                  <td
                    className={cn(
                      "px-3 py-1.5 text-right tabular-nums font-medium",
                      t.e2e_ms >= 2000
                        ? "text-rose-600 dark:text-rose-400"
                        : t.e2e_ms >= 1200
                        ? "text-amber-600 dark:text-amber-400"
                        : "text-emerald-600 dark:text-emerald-400",
                    )}
                  >
                    {fmt(t.e2e_ms)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function LatencyStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-muted/50 p-2.5">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="text-lg font-semibold tabular-nums">{value}</p>
    </div>
  );
}

/** What an outside app sent with this call through the public API: the key
 * it used, the prompt variables and its own metadata. */
function ApiRequestCard({ call }: { call: Call }) {
  const context = (call.metadata as { context?: Record<string, unknown> } | null)?.context as
    | { source?: string; api_key?: { name?: string }; variables?: Record<string, unknown>; metadata?: unknown }
    | undefined;
  if (!context || context.source !== "api") return null;
  const variables = Object.entries(context.variables ?? {});
  const meta = context.metadata && typeof context.metadata === "object" ? context.metadata : null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Started through the API · {context.api_key?.name ?? "API key"}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">Variables</p>
          {variables.length === 0 ? (
            <p className="text-sm text-muted-foreground">None sent.</p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {variables.map(([key, value]) => (
                <div key={key} className="rounded-lg border px-3 py-2">
                  <p className="font-mono text-xs text-muted-foreground">{key}</p>
                  <p className="mt-0.5 text-sm font-medium">{String(value ?? "") || "—"}</p>
                </div>
              ))}
            </div>
          )}
        </div>
        {meta && Object.keys(meta).length > 0 && (
          <div>
            <p className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">Metadata</p>
            <pre className="overflow-x-auto rounded-md bg-muted p-3 text-xs">{JSON.stringify(meta, null, 2)}</pre>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** Values the assistant's Analysis-tab structured outputs extracted from this
 * call (assistant calls only; stored in call metadata). */
function StructuredOutputsCard({ call }: { call: Call }) {
  const outputs = (call.metadata as { structured_outputs?: Record<string, unknown> } | null)
    ?.structured_outputs;
  if (!outputs || typeof outputs !== "object" || Object.keys(outputs).length === 0) {
    return null;
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Structured Outputs</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Object.entries(outputs).map(([key, value]) => (
            <div key={key} className="rounded-lg border px-3 py-2">
              <p className="text-xs capitalize text-muted-foreground">
                {key.replace(/_/g, " ")}
              </p>
              <p className="mt-0.5 text-sm font-medium">
                {value === null || value === undefined || value === ""
                  ? "—"
                  : typeof value === "boolean"
                  ? value
                    ? "Yes"
                    : "No"
                  : String(value)}
              </p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function FollowupStatusCard({ call }: { call: Call }) {
  const status = call.followup_status || "pending";
  const send = useSendCallFollowup(call.id);
  const channels = call.followup_channels || [];

  const meta: Record<
    string,
    { label: string; tone: string; icon: typeof CheckCircle2 }
  > = {
    pending: {
      label: "Not sent yet",
      tone: "text-muted-foreground",
      icon: Clock,
    },
    processing: {
      label: "Sending…",
      tone: "text-muted-foreground",
      icon: Loader2,
    },
    sent: {
      label:
        channels.length > 0
          ? `Recap sent via ${channels.join(" + ")}`
          : "Recap sent",
      tone: "text-emerald-600",
      icon: CheckCircle2,
    },
    skipped: {
      label: "Follow-up skipped — no summary content to send",
      tone: "text-muted-foreground",
      icon: Clock,
    },
    disabled: {
      label: "Follow-up not enabled for this agent",
      tone: "text-muted-foreground",
      icon: Clock,
    },
    failed: {
      label: "Follow-up failed to send",
      tone: "text-destructive",
      icon: AlertCircle,
    },
  };
  const m = meta[status] ?? meta.pending;
  const Icon = m.icon;
  const hasSent = status === "sent";

  async function onSend() {
    try {
      const res = await send.mutateAsync();
      if (res.followup_status === "sent") {
        toast.success(
          res.followup_channels?.length
            ? `Recap sent via ${res.followup_channels.join(" + ")}`
            : "Recap sent",
        );
      } else if (res.followup_status === "disabled") {
        toast.error("Follow-up is off for this agent — turn it on first.");
      } else {
        toast.error(res.followup_error || "Recap could not be sent.");
      }
    } catch {
      toast.error("Failed to send recap.");
    }
  }

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between space-y-0">
        <CardTitle className="text-base">Follow-up</CardTitle>
        <Button
          variant="outline"
          size="sm"
          onClick={onSend}
          disabled={send.isPending}
        >
          {send.isPending ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Send className="size-4" />
          )}
          {hasSent ? "Resend recap" : "Send recap now"}
        </Button>
      </CardHeader>
      <CardContent className="space-y-1.5">
        <div className={cn("flex items-center gap-2 text-sm font-medium", m.tone)}>
          <Icon
            className={cn("size-4", status === "processing" && "animate-spin")}
          />
          {m.label}
        </div>
        {call.followup_sent_at && hasSent && (
          <p className="text-xs text-muted-foreground">
            {new Date(call.followup_sent_at).toLocaleString()}
          </p>
        )}
        {call.followup_error && !hasSent && (
          <p className="text-xs text-muted-foreground">{call.followup_error}</p>
        )}
      </CardContent>
    </Card>
  );
}

function CallInfoCard({ call }: { call: Call }) {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(
    () => () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    },
    [],
  );

  function togglePlay() {
    if (!call.recording_url) return;
    if (playing && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
      setPlaying(false);
      return;
    }
    const url = call.recording_url.startsWith("http")
      ? call.recording_url
      : `${window.location.origin}${call.recording_url}`;
    const audio = new Audio(url);
    audioRef.current = audio;
    audio.onended = () => {
      setPlaying(false);
      audioRef.current = null;
    };
    audio.onerror = () => {
      setPlaying(false);
      audioRef.current = null;
    };
    audio.play().catch(() => setPlaying(false));
    setPlaying(true);
  }

  return (
    <Card>
      <CardContent className="space-y-4 p-6">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3 lg:grid-cols-4">
          <Field label="From" value={call.from_number || "—"} bold />
          <Field label="To" value={call.to_number || "—"} bold />
          <FieldNode label="Direction">
            <span
              className={cn(
                "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium capitalize",
                call.direction === "inbound"
                  ? "text-blue-600 bg-blue-50 dark:bg-blue-950/40 dark:text-blue-400"
                  : call.direction === "outbound"
                  ? "text-purple-600 bg-purple-50 dark:bg-purple-950/40 dark:text-purple-400"
                  : "text-muted-foreground bg-muted",
              )}
            >
              <ArrowLeftRight className="size-3" />
              {call.direction || "—"}
            </span>
          </FieldNode>
          <FieldNode label="Status">
            <StatusBadge status={call.status} />
          </FieldNode>
          <FieldNode label="Channel">
            {call.channel === "whatsapp" ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400">
                <MessageCircle className="size-3" />
                WhatsApp
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                <Phone className="size-3" />
                Phone
              </span>
            )}
          </FieldNode>
          <Field label="Duration" value={fmtDuration(call.duration_seconds)} />
          <Field
            label={call.assistant_name ? "Assistant" : "Agent"}
            value={call.assistant_name || call.agent_name || call.agent?.name || "—"}
          />
          <Field
            label="Date"
            value={fmtDate(call.started_at || call.created_at)}
          />
        </dl>

        {call.recording_url && (
          <Button variant="outline" size="sm" onClick={togglePlay}>
            {playing ? (
              <>
                <Square className="size-4" />
                Stop
              </>
            ) : (
              <>
                <Play className="size-4" />
                Play Recording
              </>
            )}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function Field({
  label,
  value,
  bold,
}: {
  label: string;
  value: string;
  bold?: boolean;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className={cn("text-sm", bold && "font-semibold")}>{value}</dd>
    </div>
  );
}

function FieldNode({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd>{children}</dd>
    </div>
  );
}
